export interface PriceOptions {
  maximumFractionDigits?: number
}

/**
 * Represents a price with cents and raw values.
 */
export class Price {
  public initialCents = 0
  public cents = 0
  public raw = 0
  private maximumFractionDigits = 4
  private percentage = 0

  /**
   * Creates a new Price instance.
   * @param cents The initial number of cents.
   * @param options Optional configuration for the price.
   */
  constructor(cents: number, options?: PriceOptions) {
    // prepare options
    this.maximumFractionDigits = options?.maximumFractionDigits ?? 4

    // set initial cents
    this.initialCents = cents

    // transform
    this.transformCents(cents)
  }

  /**
   * Returns an instance of Intl.NumberFormat with the specified options.
   * @returns {Intl.NumberFormat} The Intl.NumberFormat instance.
   */
  private get intl() {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      useGrouping: true,
      style: 'decimal',
      maximumFractionDigits: this.maximumFractionDigits,
    })
  }

  /**
   * Calculates the total cents with a percentage added.
   * @param cents The initial number of cents.
   * @returns The total cents with the percentage added.
   */
  private getCentsWithPercentage(cents: number): number {
    cents = Math.round(cents)
    const percentageDecimal = this.percentage / 100
    const resultBeforeRounding = cents + cents * percentageDecimal
    const roundedResult = Math.round(Number(resultBeforeRounding))

    return roundedResult
  }

  /**
   * Slices the given number of cents by the specified number of decimal places.
   *
   * @param cents - The number of cents to be sliced.
   * @param decimal - The number of decimal places to slice the cents by. Defaults to the maximum fraction digits.
   * @returns The sliced number.
   */
  private sliceByFractionDigits(cents: number): number {
    const floatValue: number = cents / Math.pow(10, this.maximumFractionDigits)
    const price = parseFloat(floatValue.toFixed(this.maximumFractionDigits))
    const parts = new Intl.NumberFormat('pt-BR', {
      maximumFractionDigits: 2,
    }).formatToParts(price)
    const allIntegers = parts
      .filter((part) => part.type === 'integer')
      .map((i) => i.value)
      .join('')
    const fraction = parts.find((item) => item.type === 'fraction')?.value ?? 0
    const result = Number(`${allIntegers}.${fraction}`)

    return result
  }

  /**
   * Transforms the given number of cents and updates the internal state of the Price object.
   * If the given value is NaN, sets the cents and raw values to 0.
   * @param cents The number of cents to transform.
   * @returns void
   */
  private transformCents(cents: number): void {
    if (Number.isNaN(cents)) {
      this.cents = 0
      this.raw = 0
      return
    }
    const newCents = this.getCentsWithPercentage(cents)

    this.raw = newCents
    this.cents = newCents
  }

  /**
   * Updates the price with a new number of cents.
   * @param cents The new number of cents.
   * @returns The updated Price instance.
   */
  update(cents: number) {
    this.transformCents(cents)
    return this
  }

  /**
   * Sets the percentage of the price.
   * @param value The percentage value.
   * @returns The updated Price instance.
   */
  percentageOf(value: number) {
    this.percentage = value

    return this.update(this.initialCents)
  }

  /**
   * Gets the price as a floating-point number.
   */
  get float(): number {
    return this.sliceByFractionDigits(this.cents)
  }

  /**
   * Gets the price as a formatted string.
   */
  get text(): string {
    return this.intl.format(this.float)
  }

  /**
   * Gets the price as a currency string.
   */
  get currency(): string {
    return `€ ${this.text}`
  }

  get isZero(): boolean {
    return this.cents === 0
  }
}
